import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { formatCurrency, setCurrencyConfig, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  ShoppingBag,
  ChefHat,
  X,
  Send,
  Search,
} from "lucide-react";

const PublicMenuPage = () => {
  const { tableId } = useParams();
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [orderSent, setOrderSent] = useState(false);

  useEffect(() => {
    fetchMenu();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/menu`, { params: { table_id: tableId } });
      setMenu(response.data);
      const ci = response.data.currency_info;
      if (ci) {
        const rate = ci.currency === "AOA" ? 1 : (ci.exchange_rates?.[ci.currency] || 1);
        setCurrencyConfig({ currency: ci.currency, symbol: ci.currency_symbol, rate });
      }
    } catch (error) {
      toast.error("Erro ao carregar menu");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.product_id === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.23;
  const total = subtotal + tax;

  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error("Carrinho vazio");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/public/order`, null, {
        params: {
          table_id: tableId,
          customer_name: customerName || undefined,
        },
        data: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
      });

      toast.success("Pedido enviado com sucesso!");
      setOrderSent(true);
      setCart([]);
    } catch (error) {
      toast.error("Erro ao enviar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = menu.products.filter((product) => {
    const matchesCategory = selectedCategory === "all" || product.category_id === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">A carregar menu...</p>
        </div>
      </div>
    );
  }

  if (orderSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold font-['Outfit'] mb-2">Pedido Enviado!</h2>
            <p className="text-muted-foreground mb-6">
              O seu pedido foi recebido e está a ser preparado. Aguarde na sua mesa.
            </p>
            <Button onClick={() => setOrderSent(false)} className="w-full">
              Fazer Novo Pedido
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="public-menu-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold font-['Outfit']">Menu</h1>
              <p className="text-sm text-muted-foreground">Mesa {tableId?.split("_")[1] || tableId}</p>
            </div>
            <Badge variant="outline" className="gap-1">
              <ShoppingBag className="w-3 h-3" />
              {cart.reduce((sum, item) => sum + item.quantity, 0)}
            </Badge>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Categories */}
        <div className="max-w-4xl mx-auto px-4 pb-3">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex gap-2 pb-2">
              <Button
                variant={selectedCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("all")}
              >
                Todos
              </Button>
              {menu.categories.map((cat) => (
                <Button
                  key={cat.category_id}
                  variant={selectedCategory === cat.category_id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.category_id)}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProducts.map((product) => (
            <Card key={product.product_id} className="overflow-hidden">
              <div className="flex">
                {product.image_url && (
                  <div className="w-24 h-24 shrink-0 bg-muted">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="flex-1 p-3 flex flex-col justify-between">
                  <div>
                    <h3 className="font-semibold text-sm">{product.name}</h3>
                    {product.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {product.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-primary font-mono">
                      {formatCurrency(product.price)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => addToCart(product)}
                      className="h-8"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>Nenhum produto encontrado</p>
          </div>
        )}
      </div>

      {/* Cart Footer */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            {/* Cart Items */}
            <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
              {cart.map((item) => (
                <div key={item.product_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, -1)}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, 1)}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                    <span className="text-sm truncate max-w-[120px]">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono">
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => removeFromCart(item.product_id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Name Input */}
            <Input
              placeholder="Seu nome (opcional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="mb-3"
            />

            {/* Total & Submit */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total (c/ IVA)</p>
                <p className="text-xl font-bold font-mono text-primary">{formatCurrency(total)}</p>
              </div>
              <Button
                size="lg"
                onClick={handleSubmitOrder}
                disabled={submitting}
                className="gap-2"
              >
                {submitting ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Enviar Pedido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicMenuPage;
